import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faVolumeUp, 
  faVolumeMute,
  faFileAlt,
  faTruck,
  faEnvelope,
  faExclamationTriangle,
  faCheck,
  faDollarSign,
  faFile,
  faInbox,
  faArchive,
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSLAAlerts, getAlertTypeLabel, getSeverityColor, SLAAlert, useMarkAlertAsViewed } from '../hooks/useSLAAlerts';
import { useGeneralNotifications, GeneralNotification } from '../hooks/useGeneralNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { logger } from '../utils/logger';

interface NotificationBellProps {
  className?: string;
  isMobile?: boolean;
}

export default function NotificationBell({ className = '', isMobile = false }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'received' | 'archived'>('received');
  const [audioEnabled, setAudioEnabled] = useState(() => {
    const saved = localStorage.getItem('notification-audio-enabled');
    return saved !== null ? saved === 'true' : true; // Default to enabled
  });
  const [readNotifications, setReadNotifications] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('read-notifications');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const previousCountRef = useRef<number>(0);

  // SLA Alerts
  const { data: unreadSLAAlerts, refetch: refetchSLA } = useSLAAlerts('active', true);
  const { data: allSLAAlerts, isLoading: slaLoading, refetch: refetchSLAAll } = useSLAAlerts('active');
  const { markMultipleAsViewed } = useMarkAlertAsViewed();

  // General Notifications
  const { data: generalNotifications = [], isLoading: generalLoading, refetch: refetchGeneral } = useGeneralNotifications();

  // When opening the dropdown, refetch so notifications are up to date
  useEffect(() => {
    if (isOpen) {
      refetchSLA();
      refetchSLAAll();
      refetchGeneral();
    }
  }, [isOpen, refetchSLA, refetchSLAAll, refetchGeneral]);

  // Filter out read general notifications
  const unreadGeneralNotifications = generalNotifications.filter(
    notif => !readNotifications.has(notif.id)
  );

  const slaCount = unreadSLAAlerts?.length || 0;
  const generalCount = unreadGeneralNotifications.length;
  const totalCount = slaCount + generalCount;

  const isLoading = slaLoading || generalLoading;

  // Play notification sound function
  const playNotificationSound = useCallback(() => {
    if (!audioEnabled) return;
    
    try {
      // Create new audio element each time to ensure clean playback
      // This prevents issues with browser caching or audio state
      const audio = new Audio('/mm9.mp3');
      audio.volume = 0.5;
      
      // Reset to beginning
      audio.currentTime = 0;
      
      // Play and handle errors
      audio.play().catch((error) => {
        logger.warn('Failed to play notification sound:', error);
      });
      
      // Clean up after playback completes
      audio.addEventListener('ended', () => {
        audio.remove();
      }, { once: true });
    } catch (error) {
      logger.warn('Error playing notification sound:', error);
    }
  }, [audioEnabled]);

  // Play sound when new notifications arrive
  useEffect(() => {
    // Play sound if:
    // 1. Audio is enabled
    // 2. There are notifications (totalCount > 0)
    // 3. Count increased from previous (new notification arrived)
    if (audioEnabled && totalCount > 0 && totalCount > previousCountRef.current) {
      playNotificationSound();
    }
    previousCountRef.current = totalCount;
  }, [totalCount, audioEnabled, playNotificationSound]);

  // Save audio preference
  useEffect(() => {
    localStorage.setItem('notification-audio-enabled', String(audioEnabled));
  }, [audioEnabled]);

  // Save read notifications
  useEffect(() => {
    localStorage.setItem('read-notifications', JSON.stringify(Array.from(readNotifications)));
  }, [readNotifications]);

  // Mark general notification as read
  const markGeneralNotificationAsRead = (notificationId: string) => {
    setReadNotifications(prev => {
      const updated = new Set(prev);
      updated.add(notificationId);
      return updated;
    });
  };

  // Marcar alertas como vistas cuando se abre el dropdown
  useEffect(() => {
    if (isOpen && unreadSLAAlerts && unreadSLAAlerts.length > 0) {
      const unreadAlertIds = unreadSLAAlerts
        .filter(alert => !alert.viewed_at)
        .map(alert => alert.id);
      
      if (unreadAlertIds.length > 0) {
        markMultipleAsViewed(unreadAlertIds)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['sla-alerts'] });
          })
          .catch(() => {
            // Error marking alerts as viewed - will retry on next open
          });
      }
    }
  }, [isOpen, unreadSLAAlerts, markMultipleAsViewed, queryClient]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (isLoading) {
    return null;
  }

  const allNotifications: Array<{ type: 'sla' | 'general'; data: SLAAlert | GeneralNotification }> = [
    ...(allSLAAlerts || []).map(alert => ({ type: 'sla' as const, data: alert })),
    ...generalNotifications.map(notif => ({ type: 'general' as const, data: notif })),
  ].sort((a, b) => {
    const dateA = new Date(a.type === 'sla' ? a.data.created_at : a.data.created_at).getTime();
    const dateB = new Date(b.type === 'sla' ? b.data.created_at : b.data.created_at).getTime();
    return dateB - dateA;
  });

  const receivedNotifications = allNotifications.filter(n => {
    if (n.type === 'sla') return !(n.data as SLAAlert).viewed_at;
    return !readNotifications.has((n.data as GeneralNotification).id);
  });
  const archivedNotifications = allNotifications.filter(n => {
    if (n.type === 'sla') return !!(n.data as SLAAlert).viewed_at;
    return readNotifications.has((n.data as GeneralNotification).id);
  });
  const totalNotificationCount = allNotifications.length;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Botón de Campanita */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative p-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${isMobile 
            ? 'text-gray-700 hover:bg-gray-100' 
            : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm'
          }
          ${isOpen ? 'bg-white shadow-sm' : ''}
        `}
        aria-label={`Notifications${totalCount > 0 ? ` (${totalCount} notifications)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FontAwesomeIcon 
          icon={faBell} 
          className={`w-5 h-5 ${totalCount > 0 ? 'text-amber-500' : ''}`}
        />
        
        {/* Badge con contador */}
        {totalCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
          >
            {totalCount > 9 ? '9+' : totalCount}
          </motion.span>
        )}
      </button>

      {/* Dropdown de Notificaciones */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`
              absolute top-full mt-2 right-0
              ${isMobile 
                ? 'w-[calc(100vw-5rem)] max-w-[280px]' 
                : 'w-80 sm:w-96'
              }
              bg-white rounded-xl shadow-xl border border-gray-200
              z-[100] max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col
            `}
          >
            {/* Header: Notifications + total count */}
            <div className="px-2 sm:px-3 pt-3 pb-2 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">Notifications</h3>
                <span className="text-xs text-gray-600 tabular-nums">
                  {totalNotificationCount} notification{totalNotificationCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="mt-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setAudioEnabled(!audioEnabled); }}
                  className={`p-1.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    audioEnabled ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={audioEnabled ? 'Disable sound' : 'Enable sound'}
                  aria-label={audioEnabled ? 'Disable notification sound' : 'Enable notification sound'}
                  aria-pressed={audioEnabled}
                >
                  <FontAwesomeIcon icon={audioEnabled ? faVolumeUp : faVolumeMute} className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Tabs: Received | Archived */}
            <div className="flex border-b border-gray-200 bg-gray-100/80 flex-shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab('received')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'received' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faInbox} className="w-3.5 h-3.5" />
                Received
                {receivedNotifications.length > 0 && (
                  <span className="bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px]">
                    {receivedNotifications.length > 99 ? '99+' : receivedNotifications.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('archived')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === 'archived' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faArchive} className="w-3.5 h-3.5" />
                Archived
              </button>
            </div>

            {/* List by tab - show all */}
            <div className="overflow-y-auto max-h-[60vh] flex-1 min-h-0">
              {(() => {
                const list = activeTab === 'received' ? receivedNotifications : archivedNotifications;
                if (list.length === 0) {
                  return (
                    <div className="px-3 sm:px-4 py-8 text-center">
                      <div className="text-gray-400 mb-2">
                        <FontAwesomeIcon icon={activeTab === 'received' ? faInbox : faArchive} className="w-8 h-8" />
                      </div>
                      <p className="text-sm text-gray-500">
                        {activeTab === 'received' ? 'No unread notifications' : 'No archived notifications'}
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="divide-y divide-gray-100">
                    {list.map((notification) => (
                      <NotificationItem
                        key={`${notification.type}-${notification.data.id}`}
                        notification={notification}
                        onClose={() => setIsOpen(false)}
                        isRead={notification.type === 'general' ? readNotifications.has(notification.data.id) : !!(notification.data as SLAAlert).viewed_at}
                        onMarkAsRead={notification.type === 'general' ? () => markGeneralNotificationAsRead(notification.data.id) : undefined}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            {totalCount > 0 && (
              <div className="px-2 sm:px-3 py-2 sm:py-2.5 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                  <div className="flex items-center gap-4">
                    {slaCount > 0 && (
                      <span className="flex items-center gap-1">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3 text-orange-500" />
                        {slaCount} SLA
                      </span>
                    )}
                    {generalCount > 0 && (
                      <span className="flex items-center gap-1">
                        <FontAwesomeIcon icon={faBell} className="w-3 h-3 text-blue-500" />
                        {generalCount} New
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {slaCount > 0 && (
                      <Link
                        to="/admin/sla-alerts"
                        onClick={() => setIsOpen(false)}
                        className="text-primary hover:text-primary-dark font-medium text-xs"
                      >
                        SLA Alerts →
                      </Link>
                    )}
                    <Link
                      to="/admin/quotes"
                      onClick={() => setIsOpen(false)}
                      className="text-primary hover:text-primary-dark font-medium text-xs"
                    >
                      View All →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface NotificationItemProps {
  notification: { type: 'sla' | 'general'; data: SLAAlert | GeneralNotification };
  onClose: () => void;
  isRead?: boolean;
  onMarkAsRead?: () => void;
}

function NotificationItem({ notification, onClose, isRead = false, onMarkAsRead }: NotificationItemProps) {
  const queryClient = useQueryClient();
  const { markAsViewed } = useMarkAlertAsViewed();

  if (notification.type === 'sla') {
    const alert = notification.data as SLAAlert;
    const severityClass = getSeverityColor(alert.hours_overdue);

    const handleClick = async () => {
      if (!alert.viewed_at) {
        try {
          await markAsViewed(alert.id);
          // Invalidate all SLA alerts queries to refresh counts
          queryClient.invalidateQueries({ queryKey: ['sla-alerts'] });
          // Force immediate refetch of unread alerts
          queryClient.refetchQueries({ queryKey: ['sla-alerts'] });
        } catch (error) {
          logger.error('Error marking alert as viewed:', error);
        }
      }
      onClose();
    };

    return (
      <Link
        to="/admin/sla-alerts"
        onClick={handleClick}
        className="block px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-orange-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${severityClass}`}>
                {getAlertTypeLabel(alert.alert_type)}
              </span>
              <span className="text-xs text-gray-500">
                {Math.floor(alert.hours_overdue)}h
              </span>
            </div>
            {alert.quote_id && alert.quotes && (
              <p className="text-sm font-medium text-gray-800 truncate">
                Quote: {alert.quotes.name}
              </p>
            )}
            {alert.carrier_id && alert.carrier_registrations && (
              <p className="text-sm font-medium text-gray-800 truncate">
                Carrier: {alert.carrier_registrations.company_name}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {format(new Date(alert.created_at), 'MMM dd, HH:mm')}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  const notif = notification.data as GeneralNotification;
  const iconMap: Record<string, any> = {
    quote: faFileAlt,
    quote_status_change: faDollarSign,
    carrier: faTruck,
    contact: faEnvelope,
    sla: faExclamationTriangle,
    payment: faDollarSign,
    document: faFile,
  };
  const colorMap: Record<string, string> = {
    quote: 'bg-blue-100 text-blue-600',
    quote_status_change: 'bg-purple-100 text-purple-600',
    carrier: 'bg-purple-100 text-purple-600',
    contact: 'bg-green-100 text-green-600',
    sla: 'bg-orange-100 text-orange-600',
    payment: 'bg-emerald-100 text-emerald-600',
    document: 'bg-amber-100 text-amber-600',
  };

  const getLink = () => {
    if (notif.type === 'quote' || notif.type === 'payment' || notif.type === 'quote_status_change') {
      // If link already starts with /admin/quotes, use it as is
      if (notif.link.startsWith('/admin/quotes')) {
        return notif.link;
      }
      // Otherwise, construct the link
      return `/admin/quotes${notif.link.includes('?') ? '?' + notif.link.split('?')[1] : ''}`;
    }
    if (notif.type === 'carrier' || notif.type === 'document') {
      return `/admin/carriers${notif.link.includes('?') ? '?' + notif.link.split('?')[1] : ''}`;
    }
    if (notif.type === 'contact') {
      return notif.link; // Already includes /admin/messages?message=...
    }
    return '/admin/quotes'; // Default fallback
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead();
    }
  };

  return (
    <div className={`px-4 py-3 hover:bg-gray-50 transition-colors ${isRead ? 'opacity-60' : ''}`}>
      <Link
        to={getLink()}
        onClick={onClose}
        className="block"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[notif.type]}`}>
              <FontAwesomeIcon icon={iconMap[notif.type]} className="w-4 h-4" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`text-sm font-medium ${isRead ? 'text-gray-500' : 'text-gray-800'}`}>{notif.title}</p>
              {isRead && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
                  Read
                </span>
              )}
            </div>
            <p className={`text-xs mt-0.5 truncate ${isRead ? 'text-gray-400' : 'text-gray-600'}`}>{notif.message}</p>
            <p className="text-xs text-gray-400 mt-1">
              {format(new Date(notif.created_at), 'MMM dd, HH:mm')}
            </p>
          </div>
        </div>
      </Link>
      {!isRead && onMarkAsRead && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <button
            onClick={handleMarkAsRead}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
            Mark as read
          </button>
        </div>
      )}
    </div>
  );
}
